import React, { useState, useEffect, useRef, useContext } from 'react';
import {
  submitResponse,
  advanceFollowUp,
} from '../../../services/Interviews/chatbotService';
import AudioRecorder from '../elements/AudioRecorder';
import FollowUpStep from './FollowUpStep';
import useLiveAvatar from '../../../utils/hooks/useLiveAvatar';
import {
  ArrowPathIcon,
  StopCircleIcon,
  MicrophoneIcon,
  ArrowLongRightIcon,
} from '@heroicons/react/24/solid';
import { UserContext } from '../../../utils/contexts/UserContext';

export default function QuestionStep({
  question,
  sessionId,
  onComplete,
  questionNumber,
  totalQuestions,
}) {
  const { user } = useContext(UserContext);
  const attemptsRemaining = () => {
    const attempts = localStorage.getItem(
      `attempts_${sessionId}_${question.uuid}`,
    );
    if (!attempts) {
      localStorage.setItem(`attempts_${sessionId}_${question.uuid}`, 3);
      return 3;
    }
    return parseInt(attempts);
  };

  const setAttemptsRemaining = (attempts) => {
    localStorage.setItem(`attempts_${sessionId}_${question.uuid}`, attempts);
  };
  const [audioUrl, setAudioUrl] = useState(null);
  const [transcript, setTranscript] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [recordingState, setRecordingState] = useState('idle');
  const [timer, setTimer] = useState(180);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [subState, setSubState] = useState('base'); // 'base' | 'followups'
  const [followUpChainData, setFollowUpChainData] = useState(null);

  const audioRecorderRef = useRef(null);
  const audioPlayerRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const abortControllerRef = useRef(null);
  const submittedRef = useRef(false);
  const recordingStartTimeRef = useRef(null);
  const followUpInitPromiseRef = useRef(null);

  // Instantiated here (not in FollowUpStep) so the LiveKit room / websocket
  // survive the base-question -> follow-ups sub-state transition.
  const liveAvatar = useLiveAvatar();
  const liveAvatarStop = liveAvatar.stop;

  const isDynamicFollowup =
    question?.question?.type?.code === 'DYNAMIC_FOLLOWUP';

  const timeAllotted = 3 * 60;

  useEffect(() => {
    const currentAudioRecorder = audioRecorderRef.current;
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      if (recordingState === 'recording' && currentAudioRecorder) {
        currentAudioRecorder.stopRecording();
      }
    };
  }, [recordingState]);

  useEffect(() => {
    setAudioUrl(null);
    setTranscript('');
    setRecordingState('idle');
    setTimer(timeAllotted);
    setRecordingDuration(0);
    recordingStartTimeRef.current = null;
    setSubState('base');
    setFollowUpChainData(null);
    followUpInitPromiseRef.current = null;
    liveAvatarStop();
    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);

    if (audioPlayerRef.current) {
      audioPlayerRef.current.src = '';
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    if (question && question.transcript) {
      setTranscript(question.transcript);
    }
    if (question && question.audio_file) {
      setAudioUrl(question.audio_file);
      setRecordingState('recorded_and_processed');
    }
  }, [question, timeAllotted, liveAvatarStop]);

  useEffect(() => {
    if (recordingState === 'recording') {
      timerIntervalRef.current = setInterval(() => {
        setTimer((prevTimer) => {
          if (prevTimer <= 1) {
            clearInterval(timerIntervalRef.current);
            triggerStopAndProcessAudio();
            return 0;
          }
          return prevTimer - 1;
        });
      }, 1000);
    } else {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    }
    return () => clearInterval(timerIntervalRef.current);
  }, [recordingState]);

  const handleAudioRecordingComplete = async (audioBlob) => {
    console.log('handleAudioRecordingComplete called', {
      audioBlob: !!audioBlob,
      submittedRef: submittedRef.current,
      isProcessing,
      recordingState,
    });

    if (!audioBlob || submittedRef.current || isProcessing) {
      console.log(
        'handleAudioRecordingComplete: Early return due to guard conditions',
      );
      return;
    }

    // Calculate duration
    let duration = 0;
    if (recordingStartTimeRef.current) {
      duration = Math.floor(
        (Date.now() - recordingStartTimeRef.current) / 1000,
      );
    }
    setRecordingDuration(duration);

    setIsProcessing(true);
    submittedRef.current = true;

    try {
      const response = await submitResponse({
        params: {
          sessionId,
          question,
          recordedBlob: audioBlob,
          duration,
        },
      });
      setAudioUrl(response.audio_file);
      setTranscript(response.transcript || 'Transcription not returned.');
      setRecordingState('recorded_and_processed');
      setAttemptsRemaining(attemptsRemaining() - 1);
      abortControllerRef.current = null;

      if (isDynamicFollowup) {
        // Prefetch: decide/generate follow-up 1 while the candidate reads
        // the transcript. The promise is awaited on "Continue"; a snapshot
        // without next_question means zero follow-ups for this answer.
        // prepare: true defers the realtime session to FollowUpStep mount —
        // otherwise the avatar would speak while nobody is watching and the
        // session could idle out before the candidate clicks Continue.
        followUpInitPromiseRef.current = advanceFollowUp({
          params: {
            sessionId,
            interviewQuestionUuid: question.interview_question_uuid,
            regenerate: true,
            prepare: true,
          },
        }).catch((initError) => {
          console.error('Failed to prefetch follow-up chain:', initError);
          return null;
        });
      }
    } catch (error) {
      if (error.name !== 'CanceledError') {
        console.error('Error submitting audio:', error);
        alert('Submission failed. Check console.');
        setRecordingState('idle');
      }
    } finally {
      setIsProcessing(false);
      submittedRef.current = false;
    }
  };

  const handleAudioRecordingError = (error) => {
    console.error('Audio Recording Error:', error);
    alert(
      `Could not start or continue recording: ${
        error.message || 'Please check microphone permissions and try again.'
      }`,
    );
    setIsProcessing(false);
    setRecordingState('idle');
  };

  const triggerStartAudioRecording = async () => {
    if (attemptsRemaining() <= 0 || !audioRecorderRef.current) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    setAudioUrl(null);
    setTranscript('');
    setRecordingDuration(0);
    submittedRef.current = false;

    setIsProcessing(true);
    const success = await audioRecorderRef.current.startRecording();
    if (success) {
      recordingStartTimeRef.current = Date.now();
      setRecordingState('recording');
      setTimer(timeAllotted);
      setIsProcessing(false);
    } else {
      setIsProcessing(false);
    }
  };

  const triggerStopAndProcessAudio = async () => {
    if (
      !audioRecorderRef.current ||
      recordingState !== 'recording' ||
      isProcessing
    )
      return;
    setIsProcessing(true);
    audioRecorderRef.current.stopRecording();
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }
  };

  const handleRerecordAudio = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setAudioUrl(null);
    setTranscript('');
    setRecordingState('idle');
    setTimer(timeAllotted);
    setRecordingDuration(0);
    recordingStartTimeRef.current = null;
    if (audioPlayerRef.current) {
      audioPlayerRef.current.src = '';
    }
  };

  const handleNextQuestion = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    if (isDynamicFollowup) {
      setIsProcessing(true);
      try {
        let chainData = null;
        if (followUpInitPromiseRef.current) {
          chainData = await followUpInitPromiseRef.current;
        }
        if (!chainData) {
          // Prefetch missing (e.g. resumed session) or failed: init now.
          chainData = await advanceFollowUp({
            params: {
              sessionId,
              interviewQuestionUuid: question.interview_question_uuid,
              regenerate: true,
              // Speech is delivered by FollowUpStep's realtime_session call;
              // without prepare the backend would synthesize TTS that this
              // path never plays (double billing + slower click).
              prepare: true,
            },
          });
        }
        if (!chainData?.next_question) {
          // The AI judged the base answer sufficient (or the organization's
          // limits are reached): zero follow-ups, go to the next question.
          onComplete();
          return;
        }
        setFollowUpChainData(chainData);
        setSubState('followups');
      } catch (error) {
        console.error('Failed to initialize follow-up chain:', error);
        alert('Failed to load the follow-up question. Please try again.');
      } finally {
        setIsProcessing(false);
      }
      return;
    }
    onComplete();
  };

  const formatTime = (timeInSeconds) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = timeInSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds
      .toString()
      .padStart(2, '0')}`;
  };

  if (!question) {
    return (
      <p className="text-center dark:text-gray-300">Loading question...</p>
    );
  }

  if (isDynamicFollowup && subState === 'followups' && followUpChainData) {
    return (
      <FollowUpStep
        parentQuestion={question}
        sessionId={sessionId}
        initialChainData={followUpChainData}
        liveAvatar={liveAvatar}
        questionNumber={questionNumber}
        totalQuestions={totalQuestions}
        onChainComplete={() => {
          liveAvatar.stop();
          onComplete();
        }}
      />
    );
  }

  const showAttemptsRemainingText =
    recordingState === 'idle' ||
    recordingState === 'recording' ||
    recordingState === 'recorded_and_processed';

  return (
    <div className="flex flex-col items-center gap-4 w-full max-w-3xl p-4 md:p-6">
      <AudioRecorder
        ref={audioRecorderRef}
        onRecordingComplete={handleAudioRecordingComplete}
        onRecordingError={handleAudioRecordingError}
      />

      <div className="text-center w-full">
        {/* <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400">
          {question.interview_title || "2025 INFO ADMISSIONS CYCLE INTERVIEW"}
        </p> */}
        <h1 className="text-lg text-left max-w-1/2 text-wrap h-auto overflow-auto font-semibold text-gray-500 dark:text-gray-300">
          Question {questionNumber}/{totalQuestions}
        </h1>
        <h1 className="text-xl text-left max-w-1/2 text-wrap h-auto overflow-auto font-semibold text-gray-800 dark:text-gray-200 mb-2">
          {question.question.name || 'Failed to load question'}
        </h1>
      </div>

      <div className="w-full aspect-[16/9] bg-gray-900 rounded-lg overflow-hidden shadow-lg relative">
        {question.video_file ? (
          <video
            src={question.video_file}
            key={question.video_file + '-' + questionNumber}
            controls
            autoPlay
            playsInline
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white bg-gray-700 p-4">
            <p className="text-lg text-center">
              No video provided for this question
            </p>
          </div>
        )}
        <div className="absolute top-2 left-2 md:top-4 md:left-4 bg-black bg-opacity-60 text-white px-2 py-1 md:px-3 rounded-md text-base font-semibold">
          Time Allotted: {formatTime(timeAllotted)}
        </div>
        {(recordingState === 'recording' ||
          recordingState === 'recorded_and_processed' ||
          (recordingState === 'idle' && audioUrl)) && (
          <div className="absolute top-2 right-2 md:top-4 md:right-4 bg-black bg-opacity-60 text-white px-2 py-1 md:px-3 rounded-md text-base font-mono bg-red-500">
            {formatTime(timer)}
          </div>
        )}
        {recordingState === 'recording' && (
          <div className="absolute bottom-2 left-2 md:bottom-4 md:left-4 flex items-center gap-2 bg-red-600 text-white px-2 py-1 md:px-3 rounded-md text-base font-semibold">
            <MicrophoneIcon className="w-4 h-4 md:w-5 md:h-5 animate-pulse" />
            Recording Audio...
          </div>
        )}
      </div>

      {/* <div className="w-full">
        <p className="text-base text-gray-800 dark:text-gray-200 break-words whitespace-pre-line">
          {question.question.body || 'Failed to load question'}
        </p>
      </div> */}

      <div className="w-full flex flex-row items-center gap-3">
        {recordingState === 'idle' && !audioUrl && (
          <button
            onClick={triggerStartAudioRecording}
            disabled={attemptsRemaining() <= 0 || isProcessing}
            className="w-full max-w-64 bg-blue-600 text-white px-6 py-3 rounded-lg text-sm md:text-base font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <MicrophoneIcon className="w-5 h-5" />
            {isProcessing ? 'Please wait...' : 'Start Recording Answer'}
          </button>
        )}

        {recordingState === 'recording' && (
          <button
            onClick={triggerStopAndProcessAudio}
            disabled={isProcessing}
            className="w-full max-w-64 bg-red-600 text-white px-6 py-3 rounded-lg text-sm md:text-base font-medium hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <StopCircleIcon className="w-5 h-5" />
            {isProcessing ? 'Saving...' : 'Stop & Save Answer'}
          </button>
        )}

        {recordingState === 'recorded_and_processed' && (
          <div className="flex flex-col sm:flex-row gap-3 w-full justify-between items-center">
            <div className="flex flex-col sm:flex-row gap-3 items-center">
              <button
                onClick={handleRerecordAudio}
                disabled={isProcessing || attemptsRemaining() <= 0}
                className="w-full sm:w-auto border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-6 py-3 rounded-lg text-sm md:text-base font-medium hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <ArrowPathIcon className="w-5 h-5" />
                Re-record Answer
              </button>
              {showAttemptsRemainingText && (
                <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">
                  {attemptsRemaining() > 0
                    ? `Attempts Remaining: ${attemptsRemaining()}`
                    : 'No attempts remaining.'}
                </p>
              )}
            </div>
            <button
              onClick={handleNextQuestion}
              disabled={isProcessing}
              className="w-full sm:w-auto bg-blue-600 text-white px-6 py-3 rounded-lg text-sm md:text-base font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isProcessing
                ? 'Processing...'
                : isDynamicFollowup
                ? 'Continue'
                : questionNumber === totalQuestions
                ? 'End Interview'
                : 'Next Question'}
              {!isProcessing && <ArrowLongRightIcon className="w-5 h-5" />}
            </button>
          </div>
        )}

        {showAttemptsRemainingText &&
          recordingState !== 'recorded_and_processed' && (
            <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">
              {attemptsRemaining() > 0
                ? `Attempts Remaining: ${attemptsRemaining()}`
                : 'No attempts remaining.'}
            </p>
          )}
      </div>

      {audioUrl && recordingState === 'recorded_and_processed' && (
        <div className="w-full max-w-3xl p-3 bg-gray-100 dark:bg-gray-700 rounded-lg shadow">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
            Your recorded response:
          </h4>
          <audio
            ref={audioPlayerRef}
            src={audioUrl}
            controls
            className="w-full rounded-lg"
          />
        </div>
      )}

      {transcript && (
        <div className="w-full p-3 md:p-4 bg-gray-50 dark:bg-gray-700 rounded-lg shadow">
          <h3 className="text-base md:text-lg font-semibold text-gray-700 dark:text-gray-200 mb-2">
            Transcript
          </h3>
          <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
            {isProcessing &&
            recordingState !== 'recorded_and_processed' &&
            !transcript
              ? 'Processing transcript...'
              : transcript ||
                (recordingState === 'recorded_and_processed'
                  ? 'Transcript not available.'
                  : '')}
          </p>
        </div>
      )}
    </div>
  );
}
